import {Button, Card, Col, Container, Row} from "react-bootstrap";
import {Link} from "react-router";
import {useAuth} from "../auth/AuthContext";
import {useShop} from "../components/ShopContext";
import {ClosedBetaNotice} from "../components/ClosedBetaNotice";
import {RichText} from "../components/RichText";
import {usePageMeta} from "../components/usePageMeta";
import {organization, productPath, website} from "../structuredData";
import {SiteConfigApi} from "../api/SiteConfigApi";
import {ProductsApi} from "../api/ProductsApi";
import {useAsync} from "../components/useAsync";
import {htmlSummary} from "../components/sanitizeHtml";
import {accentProps} from "../components/accent";
import logo from "../assets/logo-horizontal.png";
import logoWhite from "../assets/logo-horizontal-white.png";
import {home, products, rich} from "../i18n";
import {SITE_NAME} from "../seo";
import {ShowcaseActions, ShowcaseClosing, ShowcaseHero, ShowcaseListen} from "./showcase/Showcase";
import {Reveal} from "./showcase/Reveal";
import {TrademarkNotices, trademarksIn} from "../components/TrademarkNotices";

// The home page, a showcase page (layout/showcasePages.ts): the featured product (the one chosen in Administration >
// Settings while it's published, else the first published by name) in its hero, its
// demos and its closing panel, with the other products between. Until there are products to show, the logo and the
// tagline.
export function HomePage() {
    const {user} = useAuth();
    const {config} = useShop();
    // Not an error worth showing here when they can't be loaded: the Products page says.
    const {data: published, loading} = useAsync(async () => {
        // The featured product's SKU: without it (the API couldn't say), the first by name.
        const [list, config] = await Promise.all([ProductsApi.getPublished(), SiteConfigApi.get().catch(() => undefined)]);
        return featuredFirst(list, config?.featuredSku);
    }, []);
    const [featured, ...others] = published ?? [];
    const marks = trademarksIn(...(published ?? []).map(x => x.description));
    // Link previews show the featured product's first image.
    usePageMeta({ image: featured?.images[0]?.url, jsonLd: [organization(), website()] });

    return (
        <>
            {/* The site's name for screen readers and search engines; the page shows the products instead. */}
            <h1 className="visually-hidden">{SITE_NAME}</h1>
            {/* What an administrator wrote (Administration > Settings; none when left blank), or our own words until then.
                Only once the API has said, so one doesn't flash up before the other. */}
            {config && !config.enabled && config.closedMessage !== '' && (
                <Container className="pt-4">
                    <ClosedBetaNotice className="mb-0">
                        {config.closedMessage
                            ? <RichText html={config.closedMessage}/>
                            : rich(home.closed, { strong: text => <strong>{text}</strong> })}
                    </ClosedBetaNotice>
                </Container>
            )}

            {featured ? (
                <>
                    <ShowcaseHero product={featured} heading="h2" imageLink={productPath(featured.sku)}
                                  top={<p className="showcase-eyebrow">{home.tagline}</p>}
                                  actions={
                                      <ShowcaseActions product={featured} source="home" extra={<>
                                          <Button as={Link as any} to={productPath(featured.sku)} variant="showcase-outline" size="lg">{home.learnMore}</Button>
                                          {user && <Button as={Link as any} to="/account/licenses" variant="showcase-outline" size="lg">{home.myLicenses}</Button>}
                                      </>}/>
                                  }
                                  note={featured.beta && (
                                      <p className="showcase-note">{rich(home.betaNote, { link: text => <Link to="/account/licenses">{text}</Link> })}</p>
                                  )}/>
                    <ShowcaseListen product={featured}/>
                </>
            ) : !loading && (
                <section className="showcase-hero showcase-end">
                    <Container className="showcase-hero-text">
                        {/* The logo for the color mode (site.css shows one). */}
                        <div className="home-logo home-logo-showcase">
                            <img src={logo} alt="" width="1084" height="332" className="home-logo-light"/>
                            <img src={logoWhite} alt="" width="1084" height="332" className="home-logo-dark"/>
                        </div>
                        <p className="showcase-lead">{home.tagline}</p>
                        <div className="showcase-actions">
                            <Button as={Link as any} to="/products" variant="showcase" size="lg">{home.products}</Button>
                            {user && <Button as={Link as any} to="/account/licenses" variant="showcase-outline" size="lg">{home.myLicenses}</Button>}
                        </div>
                    </Container>
                </section>
            )}

            {others.length > 0 && (
                <section className="showcase-section" aria-labelledby="home-more-products">
                    <Container>
                        <h2 id="home-more-products" className="showcase-title">{home.moreProducts}</h2>
                        <Row className="g-4">
                            {others.map(product => (
                                <Col md={6} lg={4} key={product.id}>
                                    <Reveal className="h-100"><Card className="showcase-card h-100" {...accentProps(product.accentColor)}>
                                        {product.images[0] && <Card.Img variant="top" src={product.images[0].url} alt={product.images[0].altText} className="product-card-image"/>}
                                        <Card.Body>
                                            <Card.Title>
                                                <Link to={productPath(product.sku)} className="stretched-link text-reset text-decoration-none">{product.name}</Link>
                                                {product.beta && <span className="badge text-bg-info align-middle fs-6 ms-2">{products.closedBeta}</span>}
                                            </Card.Title>
                                            <Card.Text className="text-body-secondary">{htmlSummary(product.description)}</Card.Text>
                                        </Card.Body>
                                    </Card></Reveal>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            )}

            {featured && <ShowcaseClosing product={featured} source="home"/>}
            {marks.length > 0 && <Container className="pt-5"><TrademarkNotices marks={marks}/></Container>}
            {published && published.length > 0 && <div className="showcase-end"></div>}
        </>
    );
}

// The published products with the featured one first (the rest in the API's order, by name). A SKU that isn't among
// them (unset, blank, or a product that isn't published) leaves the order as it is: the first by name is featured.
export function featuredFirst<T extends { sku: string }>(published: T[], featuredSku?: string | null): T[] {
    const index = featuredSku ? published.findIndex(p => p.sku === featuredSku) : -1;
    return index <= 0 ? published : [published[index], ...published.slice(0, index), ...published.slice(index + 1)];
}
